import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { KpiGrid, StatusChip } from "@/components/kcc/Kpi";
import { Panel, ModuleHeader } from "@/components/kcc/Panel";
import { DataTable, statusToChip } from "@/components/kcc/DataTable";
import { KccLineChart, KccAreaChart } from "@/components/kcc/Charts";

export default function Predictive360() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/predictive/overview").then(({ data }) => setData(data)); }, []);
    const fc = (data?.forecasts || []).map((f) => ({
        date: f.date.slice(5, 10),
        cu: f.production_cu_t,
        co: f.production_co_t,
        cash: f.cash_flow_musd,
        energy: f.energy_mwh,
        water: f.water_km3,
        safety_p: f.safety_incident_prob * 100,
        env_p: f.env_exceedance_prob * 100,
        conf: f.confidence * 100,
    }));
    return (
        <div className="space-y-6">
            <ModuleHeader
                kicker="Predictive Analytics"
                title="90-day Forecasts · Equipment Failure Predictions"
                description="Forecasts for production, cash, energy, water, safety and environmental performance — plus predicted equipment failures with confidence and required approvals."
            />
            <KpiGrid kpis={data?.kpis || []} accent="purple" cols={5} />

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <Panel title="Copper & Cobalt Production Forecast" subtitle="90-day forward · tonnes/day" accent="copper">
                    <KccLineChart
                        data={fc}
                        series={[
                            { key: "cu", label: "Cu (t/d)", color: "#e88c4a" },
                            { key: "co", label: "Co (t/d)", color: "#4f8bd9" },
                        ]}
                        height={280}
                    />
                </Panel>
                <Panel title="Cash Flow Forecast" subtitle="Daily · US$ M" accent="cobalt">
                    <KccAreaChart data={fc} dataKey="cash" label="US$ M" color="#4f8bd9" height={280} />
                </Panel>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <Panel title="Safety & Environmental Risk Probability" subtitle="Daily · %" accent="amber">
                    <KccLineChart
                        data={fc}
                        series={[
                            { key: "safety_p", label: "Incident probability %", color: "#f59e0b" },
                            { key: "env_p", label: "Env exceedance %", color: "#22c55e" },
                        ]}
                        height={260}
                    />
                </Panel>
                <Panel title="Forecast Confidence" subtitle="Decays with horizon" accent="purple">
                    <KccAreaChart data={fc} dataKey="conf" label="Confidence %" color="#8b5cf6" height={260} />
                </Panel>
            </div>

            <Panel title="Equipment Failure Predictions" subtitle="Ranked by probability" accent="red">
                <DataTable
                    columns={[
                        { key: "asset_tag", label: "Asset" },
                        { key: "asset_name", label: "Name" },
                        { key: "predicted_failure_mode", label: "Predicted mode" },
                        { key: "eta_days", label: "ETA (days)" },
                        { key: "probability", label: "Probability", render: (r) => <span className={r.probability > 0.8 ? "text-kcc-red" : r.probability > 0.65 ? "text-kcc-amber" : "kcc-text-silver"}>{(r.probability * 100).toFixed(0)}%</span> },
                        { key: "confidence", label: "Confidence", render: (r) => `${(r.confidence * 100).toFixed(0)}%` },
                        { key: "recommendation", label: "Recommendation" },
                        { key: "requires_approval", label: "Human approval" },
                    ]}
                    rows={data?.predictions || []}
                    testid="predictions-table"
                />
            </Panel>
        </div>
    );
}
